import { Button, Checkbox, DatePicker, ScrollArea, Switch, Textfield, token } from "@may-i/mash-design-system";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { type ChangeEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import styled, { createGlobalStyle, css, keyframes } from "styled-components";
import type { BranchVersion } from "~/entities/branch-version/types";
import { useCreateBranchVersion } from "~/features/branch-version/hooks/use-branch-version-mutations";
import { useSelectedVersion } from "~/features/branch-version/hooks/use-selected-version";
import { useRestoreBodyPointerEvents } from "~/shared/hooks/use-restore-body-pointer-events";
import { useToast } from "~/shared/ui/toast";

const NAME_MAX_LENGTH = 50;
const DESCRIPTION_MAX_LENGTH = 200;

/** timestamp → "YYYY-MM-DD" (로컬 타임존 기준) */
function formatLocalDate(ts: number): string {
  const d = new Date(ts);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** datepicker 디스플레이 포맷 — 항상 YYYY.MM.DD 단위로 노출 (1일~말일 범위가 월 단위로 collapse 되지 않도록) */
function formatPickerDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}.${m}.${day}`;
}

function formatPickerPeriod(start?: Date, end?: Date): string {
  const s = start ? formatPickerDate(start) : "";
  const e = end ? formatPickerDate(end) : "";
  if (s && e) return s === e ? s : `${s} - ${e}`;
  if (s) return s;
  return e;
}

interface AddVersionDrawerProps {
  open: boolean;
  versions: BranchVersion[];
  onClose: () => void;
}

export function AddVersionDrawer({
  open,
  versions,
  onClose,
}: AddVersionDrawerProps) {
  const { t } = useTranslation("version");
  const { branchUid } = useParams();
  const createBranchVersion = useCreateBranchVersion(branchUid);
  const { setSelectedVersion } = useSelectedVersion();
  const { showToast } = useToast();

  const [baseVersionId, setBaseVersionId] = useState<number | null>(null);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [validFrom, setValidFrom] = useState<string>("");
  const [validTo, setValidTo] = useState<string>("");
  const [noEndDate, setNoEndDate] = useState(false);
  const [cameraUndetermined, setCameraUndetermined] = useState(false);
  const [useImmediately, setUseImmediately] = useState(false);
  // F4: 생성 후 이 버전으로 즉시 작업 (분석 초안 설정으로 이동)
  const [workImmediately, setWorkImmediately] = useState(true);
  const [selectOpen, setSelectOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const trimmedName = name.trim();
  const isFormValid =
    trimmedName.length > 0 && validFrom !== "" && (noEndDate || validTo !== "");

  const overlappingActiveVersion = useMemo(() => {
    if (!validFrom) return undefined;
    const newStart = new Date(validFrom).getTime();
    const newEnd = (noEndDate || !validTo)
      ? new Date("2100-12-31").getTime()
      : new Date(validTo).getTime();
    return versions
      .filter((v) => v.isActive)
      .find((v) => {
        const vStart = v.validFrom ? new Date(v.validFrom).getTime() : new Date("1970-01-01").getTime();
        const vEnd = v.validTo ? new Date(v.validTo).getTime() : new Date("2100-12-31").getTime();
        return newStart <= vEnd && newEnd >= vStart;
      });
  }, [validFrom, validTo, noEndDate, versions]);

  const isDirty =
    name.length > 0
    || description.length > 0
    || validFrom !== ""
    || validTo !== ""
    || baseVersionId !== null
    || noEndDate
    || cameraUndetermined;

  // Animation state
  const [shouldRender, setShouldRender] = useState(false);
  const [animating, setAnimating] = useState(false);

  useEffect(() => {
    if (open) {
      setShouldRender(true);
      requestAnimationFrame(() => setAnimating(true));
    } else {
      setAnimating(false);
      const timer = setTimeout(() => setShouldRender(false), 250);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // B7: 드로어가 내부 Radix DropdownMenu 열린 채 닫힐 때 body pointer-events:none 잔존 방어
  useRestoreBodyPointerEvents(open);

  const resetForm = () => {
    setBaseVersionId(null);
    setName("");
    setDescription("");
    setValidFrom("");
    setValidTo("");
    setNoEndDate(false);
    setCameraUndetermined(false);
    setUseImmediately(false);
    setWorkImmediately(true);
    setSelectOpen(false);
  };

  const handleClose = () => {
    if (isDirty && !isSubmitting) {
      const ok = window.confirm(
        `${t("discardConfirm.title")}\n\n${t("discardConfirm.message")}`,
      );
      if (!ok) return;
    }
    resetForm();
    onClose();
  };

  const handleCreate = async () => {
    if (!isFormValid || isSubmitting) return;
    setIsSubmitting(true);
    // PO QA 4차 ai: BE 가 create 트랜잭션에서 활성화까지 처리. FE 는 단일 호출.
    // (이전: create + 별도 activate PATCH 의 race 로 throw 가 발생했음)
    try {
      const created = await createBranchVersion.mutateAsync({
        name: trimmedName,
        description: description.trim(),
        validFrom,
        validTo: noEndDate ? "" : validTo,
        baseBranchVersionId: baseVersionId ?? undefined,
        setCameraTypeDefault: cameraUndetermined,
        isActive: useImmediately,
      });
      showToast({
        type: "success",
        title: t("toast.createSuccess.title"),
        content: t("toast.createSuccess.content"),
      });
      resetForm();
      onClose();
      // F4: 즉시 작업 → 생성된 버전을 현재 선택 버전으로 전환
      if (workImmediately) {
        setSelectedVersion(created);
      }
    } catch (err) {
      console.error("[AddVersionDrawer] create failed:", err);
      showToast({
        type: "error",
        title: t("toast.createError.title"),
        content: t("toast.createError.content"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedVersion = versions.find((v) => v.id === baseVersionId);

  if (!shouldRender) return null;

  return (
    <>
    <DatePickerPortalStyle />
    <Overlay $visible={animating} onClick={handleClose}>
      <Panel $visible={animating} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <Header>
          <HeaderTitle>{t("drawer.title")}</HeaderTitle>
        </Header>

        {/* Body */}
        <Body>
          {/* 버전 베이스 */}
          <FieldGroup>
            <FieldLabel>{t("drawer.field.base")}</FieldLabel>
            <DropdownMenu.Root open={selectOpen} onOpenChange={setSelectOpen}>
              <DropdownMenu.Trigger asChild>
                <SelectTrigger $hasValue={!!selectedVersion}>
                  <Textfield
                    size="xSmall"
                    value={selectedVersion?.name ?? ""}
                    placeholder={t("drawer.placeholder.base")}
                    endIcon={selectOpen ? "ic_chevron_up" : "ic_chevron_down"}
                  />
                </SelectTrigger>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <SelectContent sideOffset={4} align="start">
                  <SelectScrollArea>
                    <ScrollArea>
                      <SelectItemList>
                        {versions.map((v) => (
                          <SelectItem
                            key={v.id}
                            $selected={v.id === baseVersionId}
                            onClick={() => {
                              setBaseVersionId(v.id);
                              setSelectOpen(false);
                            }}
                          >
                            {v.name}
                          </SelectItem>
                        ))}
                      </SelectItemList>
                    </ScrollArea>
                  </SelectScrollArea>
                </SelectContent>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </FieldGroup>

          {/* 이름 */}
          <FieldGroup>
            <FieldLabel>{t("drawer.field.name")}</FieldLabel>
            <Textfield
              size="xSmall"
              placeholder={t("drawer.placeholder.name")}
              value={name}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setName(e.target.value.slice(0, NAME_MAX_LENGTH))
              }
            />
          </FieldGroup>

          {/* 설명 */}
          <FieldGroup>
            <FieldLabel>{t("drawer.field.description")}</FieldLabel>
            <Textfield
              size="xSmall"
              placeholder={t("drawer.placeholder.description")}
              value={description}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                setDescription(e.target.value.slice(0, DESCRIPTION_MAX_LENGTH))
              }
            />
          </FieldGroup>

          {/* 유효 기간 */}
          <FieldGroup>
            <FieldLabel>{t("drawer.field.validPeriod")}</FieldLabel>
            <ValidPeriodRow>
              <DatePickerSlot>
                <DatePicker.Root
                  autoFillEndDate
                  key={String(noEndDate)}
                  enabledRange={!noEndDate}
                  startDate={validFrom || undefined}
                  endDate={noEndDate ? undefined : (validTo || undefined)}
                  onConfirm={(start?: number, end?: number) => {
                    setValidFrom(
                      start != null
                        ? formatLocalDate(start)
                        : "",
                    );
                    if (!noEndDate) {
                      const effectiveEnd = end ?? start;
                      setValidTo(
                        effectiveEnd != null
                          ? formatLocalDate(effectiveEnd)
                          : "",
                      );
                    }
                  }}
                >
                  <StyledCalendarToggle
                    variant="outlined"
                    placeholder={t("drawer.placeholder.validPeriod")}
                    formatPeriod={formatPickerPeriod}
                  >
                    <StyledCalendar presets={[]} />
                  </StyledCalendarToggle>
                </DatePicker.Root>
              </DatePickerSlot>
              <CheckboxSlot>
                <Checkbox
                  size="small"
                  label={t("drawer.noEndDate")}
                  checked={noEndDate}
                  onCheckedChange={(checked) => {
                    setNoEndDate(checked === true);
                    if (checked) setValidTo("");
                    else setValidTo(validFrom);
                  }}
                />
              </CheckboxSlot>
            </ValidPeriodRow>
          </FieldGroup>

          {/* 추가 또는 조정 카메라 확정 */}
          <FieldGroup>
            <FieldLabel>{t("drawer.field.cameraAdjustment")}</FieldLabel>
            <SwitchRow>
              <Switch
                color="primary"
                size="small"
                checked={cameraUndetermined}
                onCheckedChange={setCameraUndetermined}
              />
              <SwitchLabel>
                {cameraUndetermined
                  ? t("drawer.cameraConfirmed.on")
                  : t("drawer.cameraConfirmed.off")}
              </SwitchLabel>
            </SwitchRow>
          </FieldGroup>

          {/* 생성 직후 즉시 사용 (5-5) */}
          <FieldGroup>
            <FieldLabel>{t("drawer.field.useImmediately")}</FieldLabel>
            <SwitchRow>
              <Switch
                color="primary"
                size="small"
                checked={useImmediately && !overlappingActiveVersion}
                onCheckedChange={setUseImmediately}
                disabled={!!overlappingActiveVersion}
              />
              <SwitchLabel $disabled={!!overlappingActiveVersion}>
                {useImmediately && !overlappingActiveVersion
                  ? t("drawer.useImmediatelyState.on")
                  : t("drawer.useImmediatelyState.off")}
              </SwitchLabel>
            </SwitchRow>
            {!!overlappingActiveVersion && (
              <OverlapWarning>
                {t("action.overlapTooltip", { name: overlappingActiveVersion?.name ?? "" })}
              </OverlapWarning>
            )}
          </FieldGroup>

          {/* F4: 생성 후 이 버전으로 즉시 작업 */}
          <FieldGroup>
            <FieldLabel>{t("drawer.field.workImmediately")}</FieldLabel>
            <SwitchRow>
              <Switch
                color="primary"
                size="small"
                checked={workImmediately}
                onCheckedChange={setWorkImmediately}
              />
              <SwitchLabel>
                {workImmediately
                  ? t("drawer.workImmediatelyState.on")
                  : t("drawer.workImmediatelyState.off")}
              </SwitchLabel>
            </SwitchRow>
          </FieldGroup>
        </Body>

        {/* Footer */}
        <Footer>
          <Button
            variant="outlined"
            color="primary"
            size="medium"
            onClick={handleClose}
          >
            {t("drawer.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="medium"
            onClick={handleCreate}
            disabled={!isFormValid || isSubmitting}
          >
            {t("drawer.create")}
          </Button>
        </Footer>
      </Panel>
    </Overlay>
    </>
  );
}

// --- Animations ---

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to { transform: translateX(0); }
`;

const slideOut = keyframes`
  from { transform: translateX(0); }
  to { transform: translateX(100%); }
`;

// --- Styles ---

const Overlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  inset: 0;
  z-index: 100;
  background-color: ${({ $visible }) =>
    $visible ? "rgba(0, 0, 0, 0.2)" : "transparent"};
  transition: background-color 0.25s ease;
  /* B7 별건: 닫힘 애니메이션(250ms) 동안 투명 오버레이가 클릭을 삼키지 않도록 게이팅 */
  pointer-events: ${({ $visible }) => ($visible ? "auto" : "none")};
`;

const Panel = styled.div<{ $visible: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 480px;
  display: flex;
  flex-direction: column;
  background-color: ${token("colors.gray.white")};
  box-shadow: -10px 2px 20px 0px rgba(0, 0, 0, 0.12);
  animation: ${({ $visible }) =>
      $visible
        ? css`${slideIn}`
        : css`${slideOut}`}
    0.25s ease forwards;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 24px;
`;

const HeaderTitle = styled.h3`
  margin: 0;
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.56;
  color: ${token("colors.text.primary")};
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 24px;
  flex: 1;
  overflow-y: auto;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 8px;
  padding: 24px;
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ValidPeriodRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const DatePickerSlot = styled.div`
  width: 100%;
`;

const CheckboxSlot = styled.div`
  align-self: flex-end;
  white-space: nowrap;
  & * {
    white-space: nowrap;
  }
`;

const FieldLabel = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.5;
  color: ${token("colors.text.secondary")};
`;

// --- Custom Select (버전 베이스) ---

const SelectTrigger = styled.div<{ $hasValue: boolean }>`
  cursor: pointer;

  > div {
    height: 32px;
  }

  > div > div {
    height: 32px;
    box-sizing: border-box;
  }

  input {
    cursor: pointer;
    caret-color: transparent;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    color: ${({ $hasValue }) => ($hasValue ? token("colors.text.primary") : token("colors.text.disabled"))};

    &::placeholder {
      color: ${token("colors.text.disabled")};
    }
  }
`;

const SelectContent = styled(DropdownMenu.Content)`
  width: var(--radix-popper-anchor-width);
  background-color: ${token("colors.gray.white")};
  border: 1px solid ${token("colors.gray.GR300")};
  border-radius: 16px;
  overflow: hidden;
  box-shadow: ${token("shadows.dropdown")};
  z-index: 101;
`;

const SelectScrollArea = styled.div`
  max-height: 224px;
`;

const SelectItemList = styled.div`
  padding: 8px 0;
`;

const SelectItem = styled(DropdownMenu.Item)<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  padding: 5px 12px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: ${token("colors.text.primary")};
  background-color: ${({ $selected }) =>
    $selected ? "rgba(114, 142, 255, 0.08)" : "transparent"};
  outline: none;

  &:hover,
  &[data-highlighted] {
    background-color: ${token("colors.transparent.GR600.16")};
  }
`;

// --- Switch ---

const SwitchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SwitchLabel = styled.span<{ $disabled?: boolean }>`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: ${({ $disabled }) => $disabled ? token("colors.text.disabled") : token("colors.text.primary")};
`;

const OverlapWarning = styled.span`
  font-family: Pretendard Variable, Pretendard, sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.5;
  color: ${token("colors.text.secondary")};
`;

// --- DatePicker ---

const DatePickerPortalStyle = createGlobalStyle`
  [data-radix-popper-content-wrapper] {
    z-index: 101 !important;
  }
`;

const StyledCalendarToggle = styled(DatePicker.CalendarToggle)`
  --date-picker-trigger-width: 100%;
`;

const StyledCalendar = styled(DatePicker.Calendar)`
  && {
    max-height: var(--radix-popover-content-available-height, 80vh);
  }
  box-shadow: ${token("shadows.dropdown")};

  /* month picker button 텍스트 중앙 정렬 보정 */
  ol li button {
    text-align: center;
  }
`;
